import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Drawer from "@mui/material/Drawer";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuIcon from "@mui/icons-material/Menu";
// Light variants: the standard wordmark's #391AA6 end vanishes on the plum rail.
import theoLogo from "../assets/img/theo_logo_light.svg";
import theoLink from "../assets/img/theo_link_light.svg";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import { useAppSelector } from "../hooks";
import { removeWebAuthTokenMirror } from "../utils/authTokenStorage";
import { useUserMsp } from "../hooks/useUserMsp";
import { canAccessUsageAnalytics, isHorizonRootRole } from "../utils/horizonRoles";
import { prefetchSupervisorList, cancelTicketPrefetch } from "../apis/ticketPrefetch";
import { isNinjaOne } from "../utils/mspUtils";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { isBillingUiVisible } from "../utils/billingAccess";
import { isCustomWorkflowsNavVisible } from "../utils/customWorkflowsAccess";
import { isSchedulerLdEnabled } from "../utils/schedulerAccess";
import { isTechnicianDashboardLdEnabled } from "../utils/technicianDashboardAccess";
import { isTicketAnalysisReportLdEnabled } from "../utils/ticketAnalysisReportAccess";
import { isTicketLifecycleLdEnabled } from "../utils/ticketLifecycleDashboardAccess";
import { isUsageInsightsLdEnabled } from "../utils/usageInsightsAccess";
import { buildSidebarSections, isFlatNavActive, navModeFromAccess, SidebarSection } from "../navigation/flatNav";
import { SectionTabDef } from "../navigation/sectionTabDefs";
import { agentNavRoster, DEFAULT_AGENT_SLUG } from "../navigation/theoAgents";
import { isAllowMultipleAgentEnabled, SINGLE_AGENT_PILOT_MESSAGE } from "../utils/multipleAgentAccess";
import { isTheoPilotEnabled } from "../utils/theoPilotAccess";
import { useAgents } from "../hooks/useAgents";
import { ORANGE, PLUM, RADIUS, RAIL_BACKGROUND, SELECTED_EDGE } from "../styles/brandTokens";

export const SIDEBAR_EXPANDED_WIDTH = 264;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

// "Dusk plum": the dark end of the wordmark gradient as the rail, orange as the "you are here" signal.
const PLUM_BG = PLUM.rail;
const INK = PLUM.ink;
const MUTED = PLUM.muted;
const GROUP = PLUM.group;
const ACTIVE = "#FFFFFF";
const ACTIVE_BG = PLUM.selected;
const ACTIVE_BG_HOVER = PLUM.selectedHover;
const ACCENT = ORANGE.main;
const ACCENT_SOFT = ORANGE.soft;
const HOVER = PLUM.hover;
const LINE = PLUM.line;

const LEAD_NAV_IDS = new Set(["work", "agents"]);
const BOTTOM_NAV_IDS = new Set(["developer", "help"]);
// Agents, Operate (theo), Insights, and Administration start expanded. A click still collapses one.
const DEFAULT_OPEN_SECTIONS = new Set(["agents", "theo", "insights", "administration"]);

const NAV_ICONS: Record<string, React.ReactNode> = {
  work: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 20 }} />,
  theo: <AutoAwesomeRoundedIcon sx={{ fontSize: 20 }} />,
  agents: <EngineeringOutlinedIcon sx={{ fontSize: 20 }} />,
  insights: <InsightsOutlinedIcon sx={{ fontSize: 20 }} />,
  administration: <BusinessOutlinedIcon sx={{ fontSize: 20 }} />,
  developer: <CodeOutlinedIcon sx={{ fontSize: 20 }} />,
  help: <HelpOutlineRoundedIcon sx={{ fontSize: 20 }} />,
};

function itemMatches(item: SectionTabDef, pathname: string, search: string): boolean {
  return item.match(pathname, search) || (item.children ?? []).some((child) => itemMatches(child, pathname, search));
}

function sectionMatches(section: SidebarSection, pathname: string, search: string): boolean {
  return isFlatNavActive(section.id, pathname) || section.children.some((child) => itemMatches(child, pathname, search));
}

interface SidebarProps {
  isSidebarExpanded: boolean;
  /** Desktop collapse/expand; the control lives in the sidebar header. */
  onToggleExpanded?: () => void;
  handleDrawerToggle: () => void;
  mobileOpen: boolean;
  showPsaSetupOnly?: boolean;
  autotaskCategories?: Array<{ groupId: string; name: string }>;
}

function isLdFlagEnabled(flags: Record<string, unknown>, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = flags[key];
    if (value === true) return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "on" || normalized === "yes" || normalized === "1") return true;
    }
  }
  return false;
}

const Sidebar: React.FC<SidebarProps> = ({
  isSidebarExpanded,
  onToggleExpanded,
  handleDrawerToggle,
  mobileOpen,
  showPsaSetupOnly = false,
  autotaskCategories = [],
}) => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth0();
  const { technicianGroupList, ninjaoneBoardList } = useAppSelector((state) => state.supervisor);
  const { userMspInfo } = useUserMsp();
  const flags = useFeatureFlag();


  const showTheoAnalytics = flags["theoanalytics"] !== false;
  const allowMultipleAgents = isAllowMultipleAgentEnabled(flags as Record<string, unknown>);
  const theoPilot = isTheoPilotEnabled(flags as Record<string, unknown>);

  const hasWorkScopes = useMemo(() => {
    const queueCount =
      userMspInfo?.msp_type && isNinjaOne(userMspInfo.msp_type)
        ? (Array.isArray(ninjaoneBoardList) ? ninjaoneBoardList.length : 0)
        : (Array.isArray(technicianGroupList) ? technicianGroupList.length : 0);
    return queueCount > 0 || autotaskCategories.length > 0;
  }, [autotaskCategories.length, ninjaoneBoardList, technicianGroupList, userMspInfo?.msp_type]);

  const flagRecord = flags as Record<string, unknown>;

  // The MSP's real agents. Until GET /agents answers, buildAgentTabs falls back to the default
  // agent alone, so the Agents section never renders empty and then reshuffles.
  const { agents } = useAgents(userMspInfo?.msp_id);
  const agentRoster = useMemo(() => (agents.length > 0 ? agentNavRoster(agents) : undefined), [agents]);
  const navItems = useMemo(
    () =>
      buildSidebarSections(
        {
          mode: navModeFromAccess(showPsaSetupOnly, userMspInfo?.msp_sku),
          hasWorkScopes,
          showAutomation: true,
          showDashboard: showTheoAnalytics,
          showUsage: canAccessUsageAnalytics(userMspInfo?.role),
          showObserver: isHorizonRootRole(userMspInfo?.role),
          showAuditLogs: canAccessUsageAnalytics(userMspInfo?.role),
        },
        {
          workflows:
            theoPilot ||
            isCustomWorkflowsNavVisible(flagRecord, {
              email: user?.email,
              mspId: userMspInfo?.msp_id,
            }),
          schedules: theoPilot || isSchedulerLdEnabled(flagRecord),
          playbooks: theoPilot || isLdFlagEnabled(flagRecord, "playbooks", "skills"),
          billing:
            isBillingUiVisible(userMspInfo),
          technician: isTechnicianDashboardLdEnabled(flagRecord),
          ticketAnalysis: isTicketAnalysisReportLdEnabled(flagRecord),
          usageInsights:
            isUsageInsightsLdEnabled(flagRecord) && canAccessUsageAnalytics(userMspInfo?.role),
          ticketLifecycle: isTicketLifecycleLdEnabled(flagRecord),
          theoPilot,
        },
        agentRoster
      ),
    [
      agentRoster,
      flagRecord,
      hasWorkScopes,
      showPsaSetupOnly,
      showTheoAnalytics,
      theoPilot,
      user?.email,
      userMspInfo?.msp_id,
      userMspInfo?.credit_billing_enabled,
      userMspInfo?.msp_sku,
      userMspInfo?.role,
    ]
  );

  const [openBranches, setOpenBranches] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const activeIds = navItems
      .filter((item) => item.children.length > 0 && sectionMatches(item, location.pathname, location.search))
      .map((item) => `section:${item.id}`);
    if (activeIds.length === 0) return;
    setOpenBranches((current) => {
      const next = { ...current };
      let changed = false;
      for (const key of activeIds) {
        if (next[key] !== true) {
          next[key] = true;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [location.pathname, location.search, navItems]);

  const rowSx = (selected: boolean) => ({
    minHeight: 32,
    px: 1,
    py: 0.25,
    borderRadius: `${RADIUS.control}px`,
    fontFamily: "var(--theo-font-sans)",
    color: selected ? ACTIVE : INK,
    backgroundColor: selected ? ACTIVE_BG : "transparent",
    boxShadow: selected ? SELECTED_EDGE : "none",
    textDecoration: "none",
    transition: "background-color 150ms ease, color 150ms ease",
    "&:hover": { backgroundColor: selected ? ACTIVE_BG_HOVER : HOVER, color: ACTIVE },
    "&.Mui-selected": { backgroundColor: ACTIVE_BG, color: ACTIVE },
    "&.Mui-selected:hover": { backgroundColor: ACTIVE_BG_HOVER },
    "&.Mui-focusVisible": { outline: `2px solid ${ACCENT}`, outlineOffset: -2, backgroundColor: HOVER },
  });

  const sectionIsOpen = (id: string, active: boolean) => {
    const key = `section:${id}`;
    if (key in openBranches) return openBranches[key];
    return active || DEFAULT_OPEN_SECTIONS.has(id);
  };

  const branchOpen = (key: string, child: SectionTabDef) => {
    if (itemMatches(child, location.pathname, location.search)) return true;
    if (key in openBranches) return openBranches[key];
    return child.id === DEFAULT_AGENT_SLUG;
  };

  const renderLeaf = (child: SectionTabDef, key: string) => {
    const selected = child.match(location.pathname, location.search);
    const lockedReason =
      child.disabledReason ?? (child.id === "create" && !allowMultipleAgents ? SINGLE_AGENT_PILOT_MESSAGE : undefined);
    if (lockedReason) {
      return (
        <Tooltip key={key} title={lockedReason} placement="right">
          <Box
            component="span"
            sx={{ display: "block" }}
            data-testid={child.id === "create" ? "create-agent-control" : `nav-locked-${child.id}`}
          >
            <ListItemButton
              disabled
              disableRipple
              aria-label={child.label}
              sx={{
                ...rowSx(false),
                opacity: 1,
                color: MUTED,
                "&.Mui-disabled": { opacity: 0.45, color: MUTED },
              }}
            >
              {child.icon === "add" ? <AddRoundedIcon sx={{ fontSize: 14, mr: 0.6, color: MUTED }} /> : null}
              <Typography
                noWrap
                sx={{
                  color: MUTED,
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: child.icon === "add" ? 12.5 : 13.5,
                  fontWeight: 500,
                }}
              >
                {child.label}
              </Typography>
            </ListItemButton>
          </Box>
        </Tooltip>
      );
    }
    return (
      <ListItemButton
        key={key}
        component={Link}
        to={child.to}
        onMouseEnter={() => { if (child.to === "/supervisor") prefetchSupervisorList(userMspInfo?.msp_type); }}
        onMouseLeave={cancelTicketPrefetch}
        onFocus={() => { if (child.to === "/supervisor") prefetchSupervisorList(userMspInfo?.msp_type); }}
        onBlur={cancelTicketPrefetch}
        selected={selected}
        disableRipple
        sx={rowSx(selected)}
      >
        {/* An action rather than a destination, so it carries a glyph and the
            muted treatment that separates it from the roster above. */}
        {child.icon === "add" ? (
          <AddRoundedIcon sx={{ fontSize: 14, mr: 0.6, color: selected ? ACTIVE : MUTED }} />
        ) : null}
        <Typography
          noWrap
          sx={{
            color: child.icon === "add" && !selected ? MUTED : "inherit",
            fontFamily: "var(--theo-font-sans)",
            // The action sits a notch below the roster it follows, so it reads
            // as secondary to the engineers rather than another one of them.
            fontSize: child.icon === "add" ? 12.5 : 13.5,
            fontWeight: selected ? 600 : 500,
          }}
        >
          {child.label}
        </Typography>
      </ListItemButton>
    );
  };

  const renderChild = (sectionId: string, child: SectionTabDef) => {
    const key = `${sectionId}:${child.id}`;
    const nested = child.children ?? [];
    if (nested.length === 0) {
      return renderLeaf(child, key);
    }
    const open = branchOpen(key, child);
    return (
      <Box key={key}>
        <ListItemButton
          disableRipple
          aria-expanded={open}
          onClick={() => setOpenBranches((current) => ({ ...current, [key]: !open }))}
          sx={{
            ...rowSx(false),
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: HOVER },
          }}
        >
          <Typography
            noWrap
            sx={{ flex: 1, color: "inherit", fontFamily: "var(--theo-font-sans)", fontSize: 13.5, fontWeight: 600 }}
          >
            {child.label}
          </Typography>
          {open ? (
            <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: MUTED }} />
          ) : (
            <ChevronRightRoundedIcon sx={{ fontSize: 18, color: MUTED }} />
          )}
        </ListItemButton>
        {open ? (
          <Box sx={{ ml: 1.5, pl: 1.25, borderLeft: `1px solid ${LINE}` }}>
            {nested.map((panel) => renderLeaf(panel, `${key}:${panel.id}`))}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderSection = (item: SidebarSection, pinned = false) => {
    const sectionActive = sectionMatches(item, location.pathname, location.search);
    if (!isSidebarExpanded) {
      return (
        <Tooltip key={item.id} title={item.label} placement="right">
          <ListItemButton
            component={Link}
            to={item.path}
            selected={sectionActive}
            aria-label={item.label}
            disableRipple
            sx={{
              ...rowSx(sectionActive),
              justifyContent: "center",
              minHeight: 40,
              px: 0,
              mb: 0.5,
            }}
          >
            <Box sx={{ display: "flex", color: sectionActive ? ACCENT : MUTED }}>{NAV_ICONS[item.id]}</Box>
          </ListItemButton>
        </Tooltip>
      );
    }
    if (item.children.length === 0) {
      return (
        <ListItemButton
          key={item.id}
          component={Link}
          to={item.path}
          selected={sectionActive}
          aria-label={item.label}
          disableRipple
          sx={{ ...rowSx(sectionActive), mb: 0.5 }}
        >
          <Box sx={{ display: "flex", color: sectionActive ? ACCENT : MUTED, mr: 1 }}>{NAV_ICONS[item.id]}</Box>
          <Typography noWrap sx={{ color: "inherit", fontFamily: "var(--theo-font-sans)", fontSize: 13.5, fontWeight: 600 }}>
            {item.label}
          </Typography>
        </ListItemButton>
      );
    }
    const open = sectionIsOpen(item.id, sectionActive);
    return (
      <Box key={item.id} sx={{ mt: pinned ? 0.5 : 0.75 }}>
        <ListItemButton
          disableRipple
          aria-expanded={open}
          onClick={() => setOpenBranches((current) => ({ ...current, [`section:${item.id}`]: !open }))}
          sx={{
            minHeight: 28,
            px: 1,
            py: 0.25,
            borderRadius: `${RADIUS.control}px`,
            "&:hover": { backgroundColor: HOVER },
            "&.Mui-focusVisible": { outline: `2px solid ${ACCENT}`, outlineOffset: -2 },
          }}
        >
          <Typography
            noWrap
            sx={{
              flex: 1,
              fontFamily: "var(--theo-font-sans)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: sectionActive ? ACCENT_SOFT : GROUP,
            }}
          >
            {item.label}
          </Typography>
          {open ? (
            <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: GROUP }} />
          ) : (
            <ChevronRightRoundedIcon sx={{ fontSize: 16, color: GROUP }} />
          )}
        </ListItemButton>
        {open ? item.children.map((child) => renderChild(item.id, child)) : null}
      </Box>
    );
  };

  const leadNav = navItems.filter((item) => LEAD_NAV_IDS.has(item.id));
  const primaryNav = navItems.filter((item) => !LEAD_NAV_IDS.has(item.id) && !BOTTOM_NAV_IDS.has(item.id));
  const bottomNav = navItems.filter((item) => BOTTOM_NAV_IDS.has(item.id));

  const collapseLabel = isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar";
  // A quiet icon in the account row, the way every other app does it - a full-width
  // labelled row gave a utility control the same weight as actual navigation.
  const collapseToggle = onToggleExpanded ? (
    <Tooltip title={collapseLabel} placement="right">
      <IconButton
        size="small"
        aria-label={collapseLabel}
        aria-expanded={isSidebarExpanded}
        onClick={onToggleExpanded}
        sx={{
          display: { xs: "none", sm: "inline-flex" },
          color: MUTED,
          "&:hover": { color: ACCENT, backgroundColor: HOVER },
          "&.Mui-focusVisible": { outline: `2px solid ${ACCENT}` },
        }}
      >
        {/* The MUI glyph draws the panel on the right; mirror it so the panel sits on the left like ours. */}
        <ViewSidebarOutlinedIcon sx={{ fontSize: 18, transform: "scaleX(-1)" }} />
      </IconButton>
    </Tooltip>
  ) : null;

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, height: "100%", minHeight: 0, fontFamily: "var(--theo-font-sans)" }}>
      <Box
        sx={{
          px: isSidebarExpanded ? 2 : 0,
          pt: 1.75,
          pb: 1.25,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isSidebarExpanded ? (
          <>
            <img src={theoLink} alt="Theo" style={{ width: 96, height: 42 }} />
            <Typography
              sx={{
                mt: -0.25,
                fontFamily: "var(--theo-font-sans)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: ACCENT,
              }}
            >
              AI Technician
            </Typography>
          </>
        ) : (
          <img src={theoLogo} alt="Theo" style={{ width: 36, height: 36 }} />
        )}
      </Box>
      <Divider sx={{ borderColor: LINE }} />
      {leadNav.length > 0 ? (
        <Box
          component="nav"
          aria-label="Service Desk and agents"
          sx={{ flexShrink: 0, maxHeight: "48%", overflowY: "auto", overflowX: "hidden", px: 1, pt: 1, pb: 0.5 }}
        >
          {leadNav.map((item) => renderSection(item))}
        </Box>
      ) : null}
      {leadNav.length > 0 && (primaryNav.length > 0 || bottomNav.length > 0) ? (
        <Divider sx={{ borderColor: LINE, mx: 1.5 }} />
      ) : null}
      <Box
        component="nav"
        aria-label="Primary"
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: 1, py: 1 }}
      >
        {primaryNav.map((item) => renderSection(item))}
      </Box>
      {bottomNav.length > 0 ? (
        <Box
          component="nav"
          aria-label="Tools and help"
          sx={{
            flexShrink: 0,
            px: 1,
            pt: 0.5,
            pb: 1,
            borderTop: `1px solid ${LINE}`,
          }}
        >
          {bottomNav.map((item, index) => renderSection(item, index === 0))}
        </Box>
      ) : null}
      {isAuthenticated && user ? (
      <Box
        sx={{
          p: isSidebarExpanded ? 1.5 : 1,
          borderTop: `1px solid ${LINE}`,
          display: "flex",
          flexDirection: "column",
          alignItems: isSidebarExpanded ? "flex-start" : "center",
          justifyContent: "center",
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {isSidebarExpanded ? (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Avatar
              src={user.picture}
              alt={user.name}
              sx={{ width: 40, height: 40, mr: 1.5, bgcolor: ACCENT, color: PLUM_BG, boxShadow: `0 0 0 2px ${LINE}` }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  lineHeight: 1.2,
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: GROUP,
                }}
              >
                Helpdesk Technician
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: "14px",
                  fontWeight: 700,
                  color: INK,
                }}
              >
                {user.name}
              </Typography>
            </Box>
            {collapseToggle}
            <IconButton
              size="small"
              aria-label="Log out"
              sx={{
                ml: 0.25,
                color: MUTED,
                "&:hover": { color: ACCENT, backgroundColor: HOVER },
                "&.Mui-focusVisible": { outline: `2px solid ${ACCENT}` },
              }}
              onClick={() => {
                removeWebAuthTokenMirror();
                logout();
              }}
            >
              <LogoutRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
            <Tooltip title={user.name || "Account"} placement="right">
              <Avatar src={user.picture} alt={user.name} sx={{ width: 32, height: 32, bgcolor: ACCENT, color: PLUM_BG }} />
            </Tooltip>
            {collapseToggle}
          </Box>
        )}
      </Box>
      ) : null}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_EXPANDED_WIDTH,
            boxSizing: "border-box",
            background: RAIL_BACKGROUND,
            color: INK,
            borderRight: "none",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          width: isSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
          flexShrink: 0,
          transition: "width 0.2s",
          [`& .MuiDrawer-paper`]: {
            width: isSidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
            boxSizing: "border-box",
            background: RAIL_BACKGROUND,
            color: INK,
            borderRight: "none",
            transition: "width 0.2s",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {drawer}
      </Drawer>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          mr: 2,
          display: { sm: "none" },
          position: "fixed",
          top: 32,
          left: 16,
          zIndex: 1200,
          color: INK,
          backgroundColor: PLUM_BG,
          boxShadow: 1,
          "&:hover": { backgroundColor: ACTIVE_BG },
        }}
      >
        <MenuIcon />
      </IconButton>
    </>
  );
};

export default Sidebar;
